import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { FeatureSetDataWithGroupIdAsIndex, FeatureSetData, RoomData, HomeData } from '../interfaces/data-analytics';
import { LdsEventBusService } from './lds-event-bus.service';
import { LwProductService } from '../lw-product/lw-product.service';
import { StorageMap } from '@ngx-pwa/local-storage';
import { Observable } from 'rxjs';
import { UserDetail, UserDetailLocalStorageItem } from '../interfaces/all';
import { time, GlobalVariablesService } from './global-variables.service';

const energyMonitorProductCodes = {
	'LW600': true,
	'VTRIGGER': {
		virtualProductCode: 'EMON_EMONTX4'
	},
}

@Injectable({
	providedIn: 'root'
})
export class LdsUserService {
	private _redirUrl;
	private expires = Date.now() + time.hour;
	private _userDetail: UserDetail;
	private _loading: boolean;
	private _features;
	public _devices;
	private _currentModule: string;
	private _devicePreference;
	private _deviceTypes;

	private _virtualDevices;

	private _rooms: RoomData[] = [];
	private _homes: HomeData[] = [];
	public _settings;
	public _energySummaryFeatureSetIds: string[] = [];
	public _energyFeatureSets: FeatureSetDataWithGroupIdAsIndex;
	private _temperatureFeatureSets: FeatureSetDataWithGroupIdAsIndex;
	private _swtichFeatureSets: FeatureSetDataWithGroupIdAsIndex;
	private _motionFeatureSets: FeatureSetDataWithGroupIdAsIndex;
	private _batteryFeatureSets = {};
	private _allDevices = [];
	public _temperatureFeatureSetsCount;
	public _switchFeatureCount;
	private _energyMonitors = {};
	public _totalWeeklyUsage;
	public _totalMonthlyUsage;
	private octopusData;
	
	constructor(private localStorage: StorageMap, private eventBus: LdsEventBusService, private lw: LwProductService,
		private cookie: CookieService, private globalVars: GlobalVariablesService) {
			globalVars.setUserService(this)

	}

	
	addBatteryFeatureSet(featureSet){
		if(!this._batteryFeatureSets){
			this._batteryFeatureSets = {};
		}

		this._batteryFeatureSets[featureSet.groupId] = featureSet;
		this.saveToLocalStorage("batteryFeatureSets", this.batteryFeatureSets)
	}

	set batteryFeatureSets(batteryFeatureSets){
		this._batteryFeatureSets = batteryFeatureSets;
	}

	get batteryFeatureSets(){
		
		return this._batteryFeatureSets;
	}


	set redirUrl(url) {
		this._redirUrl = url;
	}

	get redirUrl() {
		return this._redirUrl;
	}

	set userDetail(data: UserDetail) {
		let item = {
			expires: this.expires,
			data: data
		};
		this.localStorage.set('lds-user-info', item).subscribe(() => { });
		this._userDetail = data;
	}

	set loading(loading: boolean) {
		this._loading = loading;
	}

	set devicePreference(str) {
		this._devicePreference = str;
		this.cookie.set(this.userDetail.user._id + '-device-preference', str);
	}

	set currentModule(mod) {
		this._currentModule = mod;
	}

	get energyMonitors() {
		return this._energyMonitors;
	}

	set energyFeatureSets(energyFeatureSets) {
		this._energyFeatureSets = energyFeatureSets;
		Object.keys(energyFeatureSets).forEach(groupId => {
			this.addEnergySummaryFeatureSet(energyFeatureSets[groupId])
		})
	}

	get userDetail(): UserDetail {
		return this._userDetail;
	}

	set features(features) {
		this._features = features;
	}

	set rooms(rooms) {
		this._rooms = rooms;
	}
	
	set homes(homes) {
		this._homes = homes || [];
	}

	addHome(home, linkDeviceId) {
		const structureId = home.groupId.split('-')[0]

		let linkId = ''
		if (linkDeviceId) {
			linkId = linkDeviceId.split('-')[2]
			if (linkId) {
				linkId = linkId.split('+')[0]
			}
		}
		
		this._homes.push(Object.assign({ structureId, linkId }, home));
	}
	
	getHome(anyId) {
		const structureId = anyId.split('-')[0]
		const found = this._homes.find(home => home.structureId === structureId)
		return found;
	}

	set deviceTypes(deviceTypes) {
		this._deviceTypes = deviceTypes;
	}

	set temperatureFeatureSets(data) {
		this._temperatureFeatureSets = data;
	}

	set switchFeatureSets(data) {
		this._swtichFeatureSets = data;
	}

	set allDevices(data) {
		this._allDevices = data;
	}

	set settings(settings) {
		if (!this._settings || !this._settings.settings) {
			this._settings = {
				settings: [
					{
						label: 'Energy',
						items: [
							{
								label: 'Unit Rate',
								value: settings.items[0].payload.utility.electricity ? settings.items[0].payload.utility.electricity.tariff : 0,
								tooltip: 'Please enter a positive number.',
								rule: 'Positive number'
							},
							{
								type: 'select',
								label: 'Currency',
								value: `GBP (${String.fromCharCode(163)})`,
								available: [`EUR (${String.fromCharCode(8364)})`, `GBP (${String.fromCharCode(163)})`]
							}
						]
					}
				]
			};
		}

		this.eventBus.broadcast('user-settings-loaded', this._settings);
	}

	addVirtualDevice(device){
		if (!this._virtualDevices) {
			this._virtualDevices = [];
		}

		this._virtualDevices.push(device);
	}

	get settings() {
		return this._settings;
	}

	get features() {
		return this._features;
	}

	get devices() {
		return this._devices;
	}

	get energyFeatureSets() {
		return this._energyFeatureSets;
	}

	get energySummaryFeatureSetIds() {
		return this._energySummaryFeatureSetIds;
	}

	get temperatureFeatureSets() {
		return this._temperatureFeatureSets;
	}

	get switchFeatureSets() {
		return this._swtichFeatureSets;
	}

	get allDevices() {
		return this._allDevices;
	}

	get loading() {
		return this._loading;
	}

	get devicePreference() {
		return this._devicePreference || this.cookie.get(this.userDetail.user._id + '-device-preference') || 'devices';
	}

	get currentModule(): string {
		return this._currentModule;
	}

	get temperatureFeatureSetsCount() {
		return this._temperatureFeatureSetsCount;
	}

	get energyFeatureSetsCount() {
		return Object.keys(this._energyFeatureSets).length
	}

	get energySummaryFeatureSetsCount() {
		return this._energySummaryFeatureSetIds.length
	}

	get rooms() {
		return this._rooms;
	}

	get homes() {
		return this._homes;
	}

	get deviceTypes() {
		return this._deviceTypes;
	}

	async addRooms(rooms) {
		this._rooms = this._rooms.concat(rooms);
	}

	addFeatures(features) {
		Object.assign(this._features, features);
	}

	getRoom(featureSet){
		return this._rooms.find(room => room.featureSets.includes(featureSet.groupId));
	}

	addDevices(devices) {
		for (const key in devices) {
			if (devices.hasOwnProperty(key)) {
				const device = devices[key];

				const deviceType = this.lw.findType(device.productCode);
				if (deviceType in this._deviceTypes) {
					this._deviceTypes[deviceType] += 1;
				} else {
					this._deviceTypes[deviceType] = 1;
				}

				if (device.productCode === 'VTRIGGER') {
					this.addVirtualDevice(device);
				}
			}
		}
		Object.assign(this._devices, devices);
	}

	get motionFeatureSets() {
		return this._motionFeatureSets;
	}

	set motionFeatureSets(motionFeatureSets) {
		this._motionFeatureSets = motionFeatureSets;
	}

	addEnergySummaryFeatureSet(featureSet: FeatureSetData) {
		if (!this.isEnergyMonitor(featureSet)) {
			this._energySummaryFeatureSetIds.push(featureSet.groupId)
		}
	}

	addEnergyFeatureSet(groupId, item: FeatureSetData) {
		this.pushAllDevices(item)
		this._energyFeatureSets[groupId] = item;
		this.addEnergySummaryFeatureSet(item)
	}

	addMotionFeatureSet(key, item: FeatureSetData) {
		this.pushAllDevices(item)
		this._motionFeatureSets[key] = item;
	}

	addTemperatureFeatureSet(key, item) {
		this.pushAllDevices(item)
		this._temperatureFeatureSets[key] = item;
	}

	addSwitchFeatureSet(key, item) {
		this.pushAllDevices(item)
		this._swtichFeatureSets[key] = item;
	}

	pushAllDevices(item) {
		let newDevice = item
		let found = this.allDevices.find(d => d.deviceId === item.deviceId)
		if (found) {
			found = Object.assign({}, found, newDevice)
		}
		else this.allDevices.push(newDevice);
	}

	getUserDetail(): Promise<UserDetail> {
		return new Promise((resolve, reject) => {
			if (this._userDetail) {
				resolve(this._userDetail);
			} else {
				(this.localStorage.get('lds-user-info') as Observable<any>)
					.subscribe((res: any) => {
						if (res && res.expires > Date.now()) {
							this._userDetail = res.data;
							resolve(res.data);
						} else {
							reject();
						}
					});
			}
		});
	}

	set energyMonitors(data) {
		this._energyMonitors = data;
		this.eventBus.broadcast("energy-monitor-loaded");
	}

	saveToLocalStorage(name: string, obj: any) {
		this.localStorage
			.set(this.userDetail.user._id + '-' + name, { obj: obj, expires: this.expires + time.week * 2 })
			.subscribe(() => { console.log('saved to localstorage'); return; });
	}

	loadFromLocalStorage(name: string): Observable<any> {
		return this.localStorage.get(this.userDetail.user._id + '-' + name);
	}

	setOctopusData(serialNumber, mpan, apiKey) {
		this.octopusData = {
			serialNumber,
			mpan,
			apiKey
		}
	}

	getOctopusData() {
		return this.octopusData;
	}

	initiate() {
		this._features = {};
		this._devices = {};
		this._deviceTypes = {};
		this._energyFeatureSets = {};
		this._energySummaryFeatureSetIds = [];
		this._temperatureFeatureSets = {};
		this._motionFeatureSets = {};
		this._swtichFeatureSets = {};
		this._temperatureFeatureSetsCount = 0;
		this._switchFeatureCount = 0;
		this._rooms = [];
		this._homes = [];
		this._virtualDevices = [];
		// this._batteryFeatureSets = [];
		this._totalWeeklyUsage = 0;
		this._totalMonthlyUsage = 0;
	}

	public addEnergyMonitor(featureSet) {
		this._energyMonitors[featureSet.groupId] = featureSet;
		this.saveToLocalStorage("energyMonitors", this._energyMonitors);
		this.eventBus.broadcast("energy-monitor-loaded");
	}

	findVirtualProductCode(featureSet) : string{
		if (!featureSet) {
			return undefined;
		}

		if (!featureSet.productCode) {
			return undefined;
		}

		if (featureSet.productCode !== "VTRIGGER") {
			return featureSet.productCode;
		}

	
		let foundDevice;

		for(let i in this._virtualDevices){
			let virtualDevice = this._virtualDevices[i];
			if(virtualDevice.deviceId === featureSet.deviceId){
				foundDevice = virtualDevice;
				break;
			}
		}

		if(!foundDevice){
			return undefined;
		}

		return foundDevice.virtualProductCode;
	}

	isEnergyMonitor(featureSet) {
		if (!featureSet) return false

		let energyMonitorDef = energyMonitorProductCodes[featureSet.productCode]
		let vProductCode = this.findVirtualProductCode(featureSet)
		if (energyMonitorDef && energyMonitorDef.virtualProductCode && energyMonitorDef.virtualProductCode !== vProductCode) {
			energyMonitorDef = false
		}

		return !!energyMonitorDef
	}


}
